import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { addBoundary } from '@apia/util';
import { TDomProps, IRequiredDomProps, TUseDomProps } from './types';
import { responsiveTableStore, useResponsiveTable } from '..';

export function getDomStoreProps<ElementType extends keyof IRequiredDomProps>(
  element: ElementType,
  elementProps: IRequiredDomProps[typeof element],
  stateSelector: TUseDomProps,
) {
  const globalState = responsiveTableStore.getState();
  const tableState = stateSelector(globalState);
  const hasNonAdditionalFilters = tableState?.hasNonAdditionalFilters;
  const startDataRowIndex = hasNonAdditionalFilters ? 2 : 1;

  const statesColumns = tableState?.statesColumns ?? 0;

  let expandedBeforeThisRow = 0;

  if (element === 'tr')
    expandedBeforeThisRow = tableState?.expandedRows.filter(
      (current) => current < (elementProps as IRequiredDomProps['td']).rowIndex,
    ).length;

  let isFocused = false;
  let isSelected = false;
  switch (element) {
    case 'filterTd': {
      const castedProps = elementProps as IRequiredDomProps['filterTd'];
      isFocused =
        tableState?.focusedRow === 1 - startDataRowIndex &&
        addBoundary(tableState?.focusedColumn ?? 0, 0) - statesColumns ===
          castedProps.columnIndex + 1 + (hasNonAdditionalFilters ? 1 : 0);
      break;
    }
    case 'tr': {
      const castedProps = elementProps as IRequiredDomProps['tr'];
      isFocused = !tableState
        ? false
        : (tableState.focusedRow === castedProps.rowIndex &&
            tableState?.focusedColumn === 0) ||
          (Math.floor(tableState?.focusedRow) === castedProps.rowIndex &&
            Math.round(tableState?.focusedRow) - tableState.focusedRow !== 0 &&
            !!castedProps.isTheExpandedRow);
      isSelected =
        !castedProps.isTheExpandedRow &&
        tableState?.selectedRows.includes(castedProps.rowIndex);
      break;
    }
    case 'headerTr': {
      isFocused = !tableState
        ? false
        : tableState.focusedRow === 0 && tableState?.focusedColumn === 0;
      isSelected = false;
      break;
    }
    case 'stateCell': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      isFocused =
        tableState?.focusedRow === castedProps.rowIndex &&
        (tableState?.focusedColumn ?? 0) ===
          castedProps.columnIndex + 1 + (hasNonAdditionalFilters ? 1 : 0);
      break;
    }
    case 'additionalTd': {
      const castedProps = elementProps as IRequiredDomProps['additionalTd'];
      isFocused =
        tableState?.focusedRow === castedProps.rowIndex &&
        (tableState?.focusedColumn ?? 0) === 1;
      break;
    }
    case 'td': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      isFocused =
        tableState?.focusedRow === castedProps.rowIndex &&
        (tableState?.focusedColumn ?? 0) - statesColumns ===
          castedProps.columnIndex + 1 + (hasNonAdditionalFilters ? 1 : 0);
      break;
    }
    case 'headerStateCell': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      isFocused =
        tableState?.focusedRow ===
          castedProps.rowIndex - 1 - startDataRowIndex &&
        addBoundary(tableState?.focusedColumn, 0) ===
          castedProps.columnIndex + 1 + (hasNonAdditionalFilters ? 1 : 0);
      break;
    }
    case 'th': {
      const castedProps = elementProps as IRequiredDomProps['th'];
      isFocused =
        tableState?.focusedRow === 0 - startDataRowIndex &&
        addBoundary((tableState?.focusedColumn ?? 0) - statesColumns, 0) ===
          castedProps.columnIndex + 1 + (hasNonAdditionalFilters ? 1 : 0);
      break;
    }
    case 'separator': {
      const castedProps = elementProps as IRequiredDomProps['separator'];
      isFocused = !tableState
        ? false
        : tableState.focusedRow === castedProps.rowIndex;
      break;
    }
    default:
      break;
  }

  return {
    columns: tableState?.columns ?? [],
    expandedBeforeThisRow,
    isEditionMode: tableState?.isEditionMode,
    hasNonAdditionalFilters,
    rowsLength: element === 'table' ? tableState?.rows.length : '',
    isSelected,
    isFocused,
    startDataRowIndex,
    statesColumns: tableState?.statesColumns,
  };
}

export function getDomProps<ElementType extends keyof IRequiredDomProps>(
  state: ReturnType<typeof getDomStoreProps>,
  element: ElementType,
  elementProps: IRequiredDomProps[typeof element],
): TDomProps {
  let returnObject: Omit<TDomProps, 'ref'> = {};

  switch (element) {
    case 'filterTd': {
      const castedProps = elementProps as IRequiredDomProps['filterTd'];
      returnObject = {
        'aria-colindex':
          castedProps.columnIndex +
          1 +
          state.statesColumns +
          (state.hasNonAdditionalFilters ? 1 : 0),
        'data-editionmode': state.isEditionMode && state.isFocused,
        'data-focused': state.isFocused,
        tabIndex: state.isFocused ? 0 : -1,
        'data-rowindex': 2,
      };
      break;
    }
    case 'separator': {
      const castedProps = elementProps as IRequiredDomProps['separator'];
      returnObject = {
        'aria-rowindex':
          castedProps.rowIndex +
          state.startDataRowIndex +
          1 +
          state.expandedBeforeThisRow,
        'data-focusindex': castedProps.rowIndex + state.startDataRowIndex + 1,
        'aria-selected': state.isSelected,
        'data-focused': state.isFocused,
        'aria-expanded': false,
      };
      break;
    }
    case 'table': {
      returnObject = {
        role: 'treegrid',
        'aria-colcount': state.columns.length,
        'aria-rowcount': state.rowsLength,
      };
      break;
    }
    case 'tr': {
      const castedProps = elementProps as IRequiredDomProps['tr'];
      returnObject = {
        'aria-rowindex':
          castedProps.rowIndex +
          state.startDataRowIndex +
          1 +
          state.expandedBeforeThisRow +
          (castedProps.isTheExpandedRow ? 1 : 0),
        'data-focusindex':
          castedProps.rowIndex +
          state.startDataRowIndex +
          1 +
          (castedProps.isTheExpandedRow ? 0.5 : 0),
        'data-rowindex': castedProps.rowIndex,
        'aria-selected': state.isSelected,
        'data-focused': state.isFocused,
        'aria-expanded': castedProps.isThisRowExpanded,
        tabIndex: state.isFocused ? 0 : -1,
      };
      break;
    }
    case 'headerTr': {
      returnObject = {
        'aria-rowindex': 1,
        'data-focusindex': 1,
        'data-rowindex': state.hasNonAdditionalFilters ? -2 : -1,
        'aria-selected': false,
        'data-focused': state.isFocused,
        'aria-expanded': false,
      };
      break;
    }
    case 'td': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      returnObject = {
        'aria-colindex':
          castedProps.columnIndex +
          1 +
          state.statesColumns +
          (state.hasNonAdditionalFilters ? 1 : 0),
        'data-editionmode': state.isEditionMode && state.isFocused,
        'data-focused': state.isFocused,
        tabIndex: state.isFocused ? 0 : -1,
        'data-rowindex': castedProps.rowIndex + state.startDataRowIndex + 1,
      };
      break;
    }
    case 'additionalTd': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      returnObject = {
        'aria-colindex': 1,
        'data-editionmode': state.isEditionMode && state.isFocused,
        'data-focused': state.isFocused,
        tabIndex: state.isFocused ? 0 : -1,
        'data-rowindex': castedProps.rowIndex + state.startDataRowIndex + 1,
      };
      break;
    }
    case 'stateCell': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      returnObject = {
        'aria-colindex':
          castedProps.columnIndex + 1 + (state.hasNonAdditionalFilters ? 1 : 0),
        'data-editionmode': state.isEditionMode && state.isFocused,
        'data-focused': state.isFocused,
        tabIndex: state.isFocused ? 0 : -1,
        'data-rowindex': castedProps.rowIndex + state.startDataRowIndex + 1,
      };
      break;
    }
    case 'headerStateCell': {
      const castedProps = elementProps as IRequiredDomProps['td'];
      returnObject = {
        'aria-colindex':
          castedProps.columnIndex + 1 + (state.hasNonAdditionalFilters ? 1 : 0),
        'data-editionmode': state.isEditionMode && state.isFocused,
        'data-focused': state.isFocused,
        tabIndex: state.isFocused ? 0 : -1,
        'data-rowindex': castedProps.rowIndex,
      };
      break;
    }
    case 'th': {
      const castedProps = elementProps as IRequiredDomProps['th'];
      returnObject = {
        'aria-colindex':
          castedProps.columnIndex +
          1 +
          state.statesColumns +
          (state.hasNonAdditionalFilters ? 1 : 0),
        'aria-sort': ['ascending', 'descending', undefined][
          ['Asc', 'Desc', null].findIndex(
            (current) =>
              current === state.columns[castedProps.columnIndex].currentSorting,
          )
        ] as 'none' | 'ascending' | 'descending' | 'other' | undefined,
        'data-editionmode': state.isEditionMode && state.isFocused,
        'data-focused': state.isFocused,
        tabIndex: state.isFocused ? 0 : -1,
        'data-rowindex': 1,
      };
      break;
    }
    default:
      break;
  }

  return returnObject;
}

/**
 * Este hook extrae según el estado de la tabla actual
 * y el nombre del elemento al que se le desean calcular
 * los atributos que deben mostrarse en el dom para
 * cumplir con las reglas de accesibilidad.
 *
 * @param element Es el nombre del elemento
 *
 * @param elementProps Las propiedades necesarias que se
 * deben suministrar para lograr un cálculo apropiado de
 * las propiedades que deben mostrarse.
 *
 * @param stateSelector Es una función que acepta el estado
 * global de la aplicación y devuelve el estado de la tabla
 * actual. Este método es necesario ya que el useDomProps
 * no sabe contra qué slice va a trabajar.
 *
 * @returns un objeto que contiene las propiedades
 * del DOM que deben destructurarse en el elemento html.
 */
export function useDomProps<ElementType extends keyof IRequiredDomProps>(
  element: ElementType,
  elementProps: IRequiredDomProps[typeof element],
  stateSelector: TUseDomProps,
): TDomProps {
  const state = useResponsiveTable(() => {
    return getDomStoreProps<ElementType>(element, elementProps, stateSelector);
  }, shallowEqual);

  return useMemo(() => {
    return getDomProps(state, element, elementProps);
  }, [element, elementProps, state]);
}
